import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, Images, Link2, Loader2, MapPin } from "lucide-react";
import { api, assetUrl, errMsg } from "@/lib/api";
import { Attribution, PermissionCheck } from "@/components/Provenance";
import { ImportSources } from "@/components/ImportSources";

const Picker = ({ photos, selected, toggle, testid }) => (
  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid={testid}>
    {photos.map((p) => {
      const key = p.sourceId || p.id;
      const on = selected.includes(key);
      return (
        <button key={key} type="button" onClick={() => toggle(key)} className="text-left" data-testid={`pick-${key}`} data-selected={on}>
          <div className={`relative aspect-[4/3] overflow-hidden rounded-[2px] bg-[var(--sand)] ${on ? "ring-2 ring-[var(--charcoal)] ring-offset-2 ring-offset-[var(--bone)]" : ""}`}>
            <img src={p.thumbUrl || assetUrl(p.url)} alt={p.title || ""} loading="lazy" className="h-full w-full object-cover" />
            {on && <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[var(--charcoal)] text-[var(--bone)]"><Check size={15} /></span>}
            {p.distanceM != null && <span className="absolute bottom-1.5 left-1.5 rounded-full bg-[var(--bone)]/85 px-2 py-0.5 text-[0.6rem]">{p.distanceM} m away</span>}
          </div>
          <div className="mt-1 line-clamp-2"><Attribution provenance={p.provenance || p} /></div>
        </button>
      );
    })}
  </div>
);

export const ImportOutcome = ({ prop, onUpload, onAdded }) => {
  const [mode, setMode] = useState(null);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(false);
  const [invite, setInvite] = useState(null);
  const [licensedOn, setLicensedOn] = useState(false);
  const [listingRef, setListingRef] = useState("");
  useEffect(() => { api.get("/import/providers").then((r) => setLicensedOn(r.data.some((p) => p.id === "repliers" && p.status === "Active"))).catch(() => {}); }, []);
  const lookup = async (ref) => {
    setBusy(true);
    try { const { data: d } = await api.post(`/properties/${prop.id}/repliers`, { listing: ref }); setData((x) => ({ ...x, photos: d.photos, error: d.photos.length ? null : "This sample listing has no photos." })); setSel([]); }
    catch (e) { setData((x) => ({ ...x, error: errMsg(e, "Sample listing not found") })); }
    setBusy(false);
  };
  const rep = prop.importReport;
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const open = async (m) => {
    setMode(m); setSel([]); setData(null);
    if (m === "licensed") api.get("/import/repliers/samples").then((r) => setData({ photos: [], samples: r.data })).catch((e) => setData({ photos: [], samples: [], error: errMsg(e, "Sample listings unavailable") }));
    if (m === "nearby") api.get(`/properties/${prop.id}/nearby-photos`).then((r) => setData(r.data)).catch(() => setData({ photos: [], error: true }));
    if (m === "library") api.get("/me/photo-library").then((r) => setData({ photos: r.data.filter((p) => p.propertyId !== prop.id) }));
    if (m === "invite" && !invite) {
      const { data: d } = await api.post(`/properties/${prop.id}/invites`);
      setInvite(`${window.location.origin}${d.path}`);
    }
  };
  const reimagine = async () => {
    setBusy(true);
    try {
      if (mode === "nearby" || mode === "licensed") {
        const items = data.photos.filter((p) => sel.includes(p.sourceId)).map((p) => ({ provider: p.provider, sourceId: p.sourceId }));
        await api.post(`/properties/${prop.id}/import-photos`, { items });
      } else {
        await api.post(`/properties/${prop.id}/library-photos`, { photoIds: sel });
      }
      setMode(null);
      await onAdded();
    } catch (e) { toast(errMsg(e, "Couldn't add those photos.")); }
    setBusy(false);
  };
  const Opt = ({ m, icon: Icon, label, onClick }) => (
    <button type="button" onClick={onClick || (() => open(m))} data-testid={`import-option-${m}`}
      className={`flex min-h-[64px] items-center gap-3 rounded-[3px] border px-4 text-left text-sm transition-colors duration-300 ${mode === m ? "border-[var(--charcoal)] bg-[var(--bone)]" : "border-[var(--line)] bg-white/40 hover:border-[var(--charcoal)]/50"}`}>
      <Icon size={18} className="shrink-0 text-[var(--accent)]" /> {label}
    </button>
  );

  return (
    <section className="mt-8 rounded-[4px] bg-[var(--bone-2)] p-5 sm:p-7" data-testid="import-outcome">
      {rep && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="import-sources-tried">
          <span className="eyebrow">Sources tried</span>
          {rep.tried.filter((t) => !t.result.startsWith("Skipped")).map((t) => <span key={t.provider} title={t.result} className="rounded-full border border-[var(--line)] px-2.5 py-1 text-[0.68rem]">{t.name}</span>)}
          {rep.tried.some((t) => t.result.startsWith("Skipped")) && (
            <span className="rounded-full border border-dashed border-[var(--line)] px-2.5 py-1 text-[0.68rem] text-[var(--stone-dark)]" title={rep.tried.filter((t) => t.result.startsWith("Skipped")).map((t) => `${t.name}: ${t.result}`).join("\n")} data-testid="import-skipped-feeds">
              {rep.tried.filter((t) => t.result.startsWith("Skipped")).length} licensed feeds · not connected
            </span>
          )}
          <ImportSources />
        </div>
      )}
      <p className="font-display text-2xl leading-snug" data-testid="import-no-photos-message">Listing identified. Add photos you&apos;re authorized to use to start reimagining.</p>
      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <Opt m="upload" icon={ImagePlus} label="Upload photos" onClick={() => setMode("upload")} />
        <Opt m="invite" icon={Link2} label="Invite the photo owner" />
        <Opt m="nearby" icon={MapPin} label="Nearby open-licensed photos" />
        <Opt m="library" icon={Images} label="My photo library" />
        {licensedOn && <Opt m="licensed" icon={Images} label="Sample licensed listings" />}
      </div>
      {mode === "licensed" && data && (
        <div className="mt-5" data-testid="licensed-panel">
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(listingRef); }}>
            <input className="field !min-h-[44px]" value={listingRef} onChange={(e) => setListingRef(e.target.value)} placeholder="Repliers sample listing ID or URL" data-testid="repliers-listing-input" />
            <button className="btn-primary !min-h-[44px] shrink-0" disabled={!listingRef.trim() || busy} data-testid="repliers-lookup-button">Find photos</button>
          </form>
          {data.error && <p className="mt-2 text-sm text-[#B42318]" data-testid="licensed-error">{data.error}</p>}
          {data.samples?.length > 0 && !data.photos.length && (
            <div className="no-scrollbar -mx-1 mt-3 flex gap-3 overflow-x-auto px-1 pb-2" data-testid="repliers-samples">
              {data.samples.map((s) => (
                <button key={s.mlsNumber} type="button" onClick={() => lookup(s.mlsNumber)} className="w-40 shrink-0 text-left" data-testid={`repliers-sample-${s.mlsNumber}`}>
                  <div className="aspect-[4/3] overflow-hidden rounded-[2px] bg-[var(--sand)]">{s.thumbUrl && <img src={s.thumbUrl} alt="" className="h-full w-full object-cover" />}</div>
                  <div className="mt-1 line-clamp-2 text-xs">{s.address || s.mlsNumber}</div>
                  <div className="text-[0.65rem] text-[var(--stone-dark)]">{s.photoCount} photos · sample data</div>
                </button>
              ))}
            </div>
          )}
          {data.photos.length > 0 && <Picker photos={data.photos} selected={sel} toggle={toggle} testid="photos-found-grid" />}
          {data.photos.length > 0 && (
            <div className="sticky bottom-3 mt-4 flex justify-end">
              <button className="btn-primary shadow-lg" disabled={!sel.length || busy} onClick={reimagine} data-testid="reimagine-selected-button">{busy && <Loader2 size={15} className="animate-spin" />} Reimagine selected{sel.length ? ` (${sel.length})` : ""}</button>
            </div>
          )}
        </div>
      )}
      {mode === "upload" && (
        <div className="mt-5" data-testid="import-upload-panel">
          <PermissionCheck checked={perm} onChange={setPerm} testid="workspace-permission-checkbox" />
          <button className="btn-primary mt-2" disabled={!perm} onClick={onUpload} data-testid="import-upload-button"><ImagePlus size={16} /> Choose photos</button>
        </div>
      )}
      {mode === "invite" && (
        <div className="mt-5" data-testid="invite-panel">
          <p className="text-sm text-[var(--ink-soft)]">Send this link to whoever took the photos. It can only add photos — nothing else.</p>
          <div className="mt-2 flex gap-2">
            <input readOnly value={invite || "Creating link…"} className="field !min-h-[44px] text-xs" data-testid="invite-link-input" />
            <button className="btn-primary !min-h-[44px] shrink-0" disabled={!invite} onClick={async () => { await navigator.clipboard?.writeText(invite).catch(() => {}); toast("Link copied"); }} data-testid="invite-copy-button">Copy</button>
          </div>
        </div>
      )}
      {(mode === "nearby" || mode === "library") && (
        <div className="mt-5">
          {!data && <div className="flex items-center gap-2 text-sm"><Loader2 size={15} className="animate-spin" /> {mode === "nearby" ? "Finding open-licensed photos near this address…" : "Loading your library…"}</div>}
          {data && mode === "nearby" && data.geo && <p className="text-xs text-[var(--stone-dark)]" data-testid="nearby-geo">Near {data.geo.matchedAddress} · {data.photos.length} photos · {data.geo.attribution || "US Census Geocoder"}</p>}
          {data && data.photos.length === 0 && <p className="text-sm" data-testid={`${mode}-empty`}>{mode === "nearby" ? "No open-licensed photos found nearby." : "No photos in your other properties yet."}</p>}
          {data && data.photos.length > 0 && <Picker photos={data.photos} selected={sel} toggle={toggle} testid={mode === "nearby" ? "photos-found-grid" : "library-grid"} />}
          {data && data.photos.length > 0 && (
            <div className="sticky bottom-3 mt-4 flex justify-end">
              <button className="btn-primary shadow-lg" disabled={!sel.length || busy} onClick={reimagine} data-testid="reimagine-selected-button">
                {busy && <Loader2 size={15} className="animate-spin" />} Reimagine selected{sel.length ? ` (${sel.length})` : ""}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
